import React from 'react'
import i18next from 'i18next'
import { ImageOptions } from '../../utils/image'
import { Link } from '../../utils/link'
import { slugify } from '../../utils/utils'
import { SpecialistListLayout, SpecialistProps } from '../cmsPage/blocks/block/specialistsBlock/specialistsBlockLayout'
import { DepartmentOrCentrum } from '../departmentsAndCentraOverview/departmentsAndCentraOverviewState'
import { FolderProps } from '../folders/foldersLayout'
import { useInView } from '../hooks/useInView'
import { DataLayer } from '../../utils/datalayer'

type NavigationSectionProps =
  | {
      kind: 'departmentOrCentrum'
      items: DepartmentOrCentrum[]
    }
  | {
      kind: 'specialist'
      specialists: SpecialistProps[]
      imageOptions: ImageOptions
    }
  | {
      kind: 'research'
      items: {
        department: string
        research: { title: string; topic: string; intro: string; url: string }[]
      }[]
    }
  | {
      kind: 'folder'
      folders: FolderProps[]
    }

const SectionAnchor = ({ letter }: { letter: string }) => (
  <span className="section__anchor" id={`pagination-${letter}`} aria-hidden="true" />
)
const SectionLetter = ({ letter, entity }: { letter: string; entity: string }) => (
  <h2 className="section__letter">
    <span className="sr-text">{i18next.t('a11y:entityWithTheLetter', { entity: entity })} </span>
    {letter}
  </h2>
)

export const NavigationSection = (p: NavigationSectionProps & { letter: string }) => {
  switch (p.kind) {
    case 'departmentOrCentrum':
      return (
        <section className="section">
          <SectionAnchor letter={p.letter} />
          <SectionLetter letter={p.letter} entity={i18next.t('misc:department_plural')} />
          <div className="section__inner">
            <ul className="section__list list--clickable has-icon">
              {p.items.map((item) => (
                <li className="section__item list__item" key={item.id}>
                  <Link url={item.url} className="section__link list__link">
                    <h3 className="section__title list__title">{item.title}</h3>
                    {item.intro && <p className="section__intro">{item.intro}</p>}
                    <svg className="list__icon" aria-hidden="true" width="20px" height="20px">
                      <use xlinkHref="#icon--arrow"></use>
                    </svg>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )
    case 'specialist':
      return (
        <section className="section" key={p.letter}>
          <SectionAnchor letter={p.letter} />
          <SectionLetter letter={p.letter} entity={i18next.t('misc:specialist_plural')} />
          <div className="section__inner">
            <SpecialistListLayout specialists={p.specialists} imageOptions={p.imageOptions} />
          </div>
        </section>
      )
    case 'research':
      return (
        <section className="section">
          <SectionAnchor letter={p.letter} />
          <SectionLetter letter={p.letter} entity={i18next.t('misc:studies_plural')} />
          <div className="section__inner">
            {p.items.map((item) => (
              <>
                <h3 className="section__title">{item.department}</h3>
                <ul className="section__list list--clickable has-icon">
                  {item.research.map((research) => (
                    <li className="section__item list__item">
                      <Link url={research.url} className="section__link list__link">
                        <p className="section__label label--m">{research.topic}</p>
                        <div className="section__copy">
                          <h4 className="section__title list__title">{research.title}</h4>
                          {research.intro && <p className="section__description">{research.intro}</p>}
                          <svg className="list__icon" aria-hidden="true" width="20px" height="20px">
                            <use xlinkHref="#icon--arrow"></use>
                          </svg>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </>
            ))}
          </div>
        </section>
      )
    case 'folder':
      return (
        <section className="section">
          <SectionAnchor letter={p.letter} />
          <SectionLetter letter={p.letter} entity={i18next.t('misc:folders_plural')} />
          <div className="section__inner">
            <ul className="section__list list--clickable has-icon">
              {p.folders.map((folder) => (
                <FolderCardItem key={`${slugify(folder.title)}-${folder.url}}`} folder={folder} />
              ))}
            </ul>
          </div>
        </section>
      )
  }
}

const FolderCardItem = ({ folder }: { folder: FolderProps }) => {
  const title = folder.title
  const afdeling = folder.afdeling ?? ''
  const position = folder.index ?? 0

  const { ref } = useInView<HTMLLIElement>(
    { threshold: 0.5 },
    (inView) => {
      if (inView) {
        DataLayer.folderCardView(title, afdeling, position)
      }
    }
  )

  const handleClick = () => {
    DataLayer.folderCardClick(title, afdeling, position)
  }

  return (
    <li className="section__item list__item" ref={ref} onClick={handleClick}>
      <Link
        url={folder.url}
        rel={folder.kind === 'pdf' ? 'nofollow' : undefined}
        className="section__link list__link"
      >
        <h3 className="section__title list__title">{folder.title}</h3>
        {folder.introduction && <p className="section__intro">{folder.introduction}</p>}
        <svg className="list__icon" aria-hidden="true" width="20px" height="20px">
          <use xlinkHref={`#icon--${folder.kind === 'pdf' ? 'SG-pdf' : 'arrow'}`}></use>
        </svg>
      </Link>
    </li>
  )
}
